import { useEffect, useState } from 'react';
import { ApiError, listTenantUsers } from './api/client';
import type { TenantUserResponse } from './api/types';

function describeError(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Request failed';
  // The users API lives on the auth server, whose errors use the OAuth wire shape
  // (error/error_description) rather than the /api domain shape (code/description).
  const body = e.body as Partial<{
    code: string;
    description: string;
    error: string;
    error_description: string;
  }>;
  const code = body.code ?? body.error ?? `HTTP ${e.status}`;
  const description = body.description ?? body.error_description ?? 'Request failed';
  return `${code}: ${description}`;
}

const left = { textAlign: 'left' } as const;

/**
 * The tenant's user pool (ADR-024 §3) — the Auth0-dashboard-style Users view, read-only: who has
 * signed in to this tenant's products, with their linked identity providers. Served by the auth
 * server (it owns the user store); requires an admin grant on this tenant or the internal tenant.
 */
export function UsersPanel({ tenant }: { tenant: string }) {
  const [users, setUsers] = useState<TenantUserResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setBusy(true);
      setError(null);
      try {
        const result = await listTenantUsers(tenant);
        if (!cancelled) setUsers(result);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(describeError(e));
          setUsers([]);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  return (
    <section>
      <h2>Users — {tenant}</h2>
      <p>
        Everyone who has signed in to this tenant&apos;s products. Users are pool-scoped: the same
        person in another tenant is a separate user.
      </p>
      {error !== null && <p role="alert">{error}</p>}
      {busy ? (
        <p>Loading…</p>
      ) : users.length === 0 && error === null ? (
        <p>No users in {tenant} yet.</p>
      ) : (
        users.length > 0 && (
          <table>
            <thead>
              <tr>
                <th style={left}>Email</th>
                <th style={left}>Name</th>
                <th style={left}>Signs in with</th>
                <th style={left}>First sign-in</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.displayName}</td>
                  <td>{user.providers.join(', ')}</td>
                  <td>{user.createdAt.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </section>
  );
}
